<template>
  <v-container>
    <div class="text-h4 mb-6">{{ __("Mudança massiva de status") }}</div>

    <!-- RESULTADO DA EXECUÇÃO -->
    <v-alert
      v-if="executionResult"
      type="success"
      outlined
      class="mb-6"
      dismissible
      @input="executionResult = null"
    >
      <div class="font-weight-bold mb-2">
        {{ __("Mudança massiva executada com sucesso") }}
      </div>

      <div>{{ __("Total de registros") }}: {{ executionResult.total }}</div>
      <div>{{ __("Sucesso") }}: {{ executionResult.success }}</div>
      <div>{{ __("Bloqueados") }}: {{ executionResult.blocked }}</div>
      <div>{{ __("Falhas") }}: {{ executionResult.fail }}</div>
    </v-alert>

    <v-card :loading="loading">
      <v-card-title>{{ __("Execução de mudança de status") }}</v-card-title>

      <v-divider />

      <v-card-text>
        <v-row>
          <v-col cols="12" md="6">
            <LeadStatusAutoComplete
              :label="__('Status de origem')"
              :selectedStatus="form.source_status_id"
              @change="handleSourceStatusChange"
              @selected="handleSourceStatusSelected"
              outlined
            />
          </v-col>

          <v-col cols="12" md="6">
            <LeadStatusAutoComplete
              :label="__('Status de destino')"
              :selectedStatus="form.target_status_id"
              @change="handleTargetStatusChange"
              @selected="handleTargetStatusSelected"
              outlined
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!canExecute || loading"
          @click="confirmExecution"
        >
          {{ __("Executar") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <ConfirmationDialog ref="confirmDialog" />
  </v-container>
</template>

<script>
import LeadStatusAutoComplete from "@/components/LeadStatuses/LeadStatusAutoComplete.vue";
import ConfirmationDialog from "@/components/tools/ConfirmationDialog.vue";
import i18n from "@/mixins/i18n";

import {
  runMassiveChange,
  previewCount,
} from "@/services/massive_status_change";

export default {
  components: {
    LeadStatusAutoComplete,
    ConfirmationDialog,
  },

  mixins: [i18n],

  data() {
    return {
      i18n_prefix: "massive_status_change",
      loading: false,

      executionResult: null,

      form: {
        source_status_id: null,
        target_status_id: null,
      },

      sourceStatus: null,
      targetStatus: null,
    };
  },

  computed: {
    canExecute() {
      return (
        this.form.source_status_id &&
        this.form.target_status_id &&
        this.form.source_status_id !== this.form.target_status_id
      );
    },
  },

  methods: {
    handleSourceStatusSelected(status) {
      this.sourceStatus = status;
    },

    handleTargetStatusSelected(status) {
      this.targetStatus = status;
    },

    handleSourceStatusChange(value) {
      this.form.source_status_id = value;
    },

    handleTargetStatusChange(value) {
      this.form.target_status_id = value;
    },

    resetForm() {
      this.form.source_status_id = null;
      this.form.target_status_id = null;

      this.sourceStatus = null;
      this.targetStatus = null;
    },

    buildConfirmDescription(total) {
      return `
        ${this.__("Foram encontrados", "massive_status_change")}
        <strong>${total} ${this.__("registros", "massive_status_change")}</strong>.
        ${this.__("Todos os serviços no status", "massive_status_change")}
        <strong>"${this.sourceStatus?.name}"</strong>
        ${this.__("serão alterados para", "massive_status_change")}
        <strong>"${this.targetStatus?.name}"</strong>.

        <div class="warning-box">
          <i class="mdi mdi-information warning-box__icon"></i>
          <div>
            <div class="warning-box__title">
              ${this.__("Importante", "massive_status_change")}
            </div>
            <div class="warning-box__text">
              ${this.__("Esta ação não poderá ser desfeita automaticamente", "massive_status_change")}
            </div>
          </div>
        </div>
      `;
    },

    async confirmExecution() {
      try {
        this.loading = true;

        const countResponse = await previewCount(this.form.source_status_id);
        const total = countResponse.data.total;

        this.$refs.confirmDialog.confirm(
          this.__("Confirmar mudança massiva de status", "massive_status_change"),

          async ({ close, startLoading, stopLoading }) => {
            startLoading();

            try {
              const response = await runMassiveChange({
                source_status_id: this.form.source_status_id,
                target_status_id: this.form.target_status_id,
              });

              this.executionResult = response.data;

              this.$store.commit(
                "success",
                `${response.data.total} ${this.__(
                  "registro(s) processado(s)",
                  "massive_status_change"
                )}`
              );

              this.resetForm();

              close();
            } catch (error) {
              console.error(error);

              this.$store.commit(
                "error",
                error?.response?.data?.message ||
                  this.__("Erro ao executar transição", "massive_status_change")
              );
            } finally {
              stopLoading();
            }
          },

          null,

          {
            description: this.buildConfirmDescription(total),
            confirmText: this.__("Confirmar", "massive_status_change"),
            cancelText: this.__("Cancelar", "massive_status_change"),
            color: "primary",
          }
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style>
.warning-box {
  background-color: #eef2ff;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.warning-box__icon {
  color: #3b5bdb;
  font-size: 20px;
  margin-top: 2px;
}

.warning-box__title {
  color: #3b5bdb;
  font-weight: bold;
  margin-bottom: 4px;
}

.warning-box__text {
  color: #555;
}
</style>